// 182 E — MOVED OUT OF App.tsx (src/App.tsx:658-1231@9a90064a), UNCHANGED:
// same props, same body; the header comment below travelled with it.
// `_untypedSeen` stayed in App.tsx — its only readers are App's own render.
import React, { useState } from "react";
import { loadXlsx } from "../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is read
import { findHeaderCol as findC2 } from "../lib/xlsxHeaders"; // A15, brief 65
import { FS } from "../lib/tokens";
import { ModalLayout } from "./SharedUI";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { SK, pname } from "../utils";
import { tempId } from "../lib/tempId"; // 207 §1.2
import { parseBoolCell, parseDateCell } from "../lib/importCells.mjs";
import { makeImportVocabularyReport } from "../lib/importVocabulary.mjs";
import { makeImportCell } from "../lib/importCell"; // A10, brief 65
import { ImportLoadingModal } from "./ImportLoadingModal"; // B60, brief 84
import { ImportErrorModal } from "./ImportErrorModal"; // B48, brief 84
import { batchClientDuplicates } from "../lib/coiMatch.mjs";
import Table from "./Table";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`
import ImportLossDialog from "./ImportLossDialog"; // 214 addendum 4 §25 [RULED 63] — an import asks before it loses anything
import { addRowsTaken, hasLoss, newLossPlan, type LossPlan } from "../lib/importLoss"; // 214 addendum 4 §25
import type { Translate } from "../lib/interestRefusal";

// BUG-A SWEEP — the standalone CLIENTS importer, hoisted to module scope. It
// was `const CII = () => {…}` inside App's render body, mounted as <CII />,
// with the same continuous-remount exposure as the jurisdictions importer
// above — and this one carries money: quantum, and the duplicate/overwrite
// mode the operator picks before committing a whole client list.
// Last of the four importers that carried this defect.
export function ClientsImportModal({ clImportFile2, setClImportFile2, clients, setClients, persist, toast, t }: any) {
// 05n §4 — the vocabularies this sheet's cells are checked against. Read here
// rather than threaded as props: this component is at module scope and the
// hook is the same module-level cache every other reader shares.
const _vTitle = useTaxonomy("person_title").entries;
const _vCountry = useTaxonomy("country").entries;
const _vClientType = useTaxonomy("client_type").entries;
const [rows, setRows] = useState<any>([]);
const [hdrs, setHdrs] = useState<any>([]);
const [parsed, setParsed] = useState<any>(false);
const [error, setError] = useState<any>(null);
const [mode, setMode] = useState<any>("add");
const [dupMode, setDupMode] = useState<any>("skip");
const [summary, setSummary] = useState<any>(null);
// 214 addendum 4 §25 [RULED 63] — the window's plan and the write it waits for
const [lossAsk, setLossAsk] = useState<{ plan: LossPlan; run: () => void } | null>(null);
useState<any>(() => {
  const reader = new FileReader();
  reader.onload = async (ev) => {
    try {
      const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
      const wb2 = XLSX.read(ev.target!.result, { type: "array" });
      const ws2 = wb2.Sheets[wb2.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws2, { defval: "" });
      if (!json.length) {
        setError(t("app.import.empty_file", { fallback: "Empty file" }));
        return;
      }
      setHdrs(Object.keys(json[0] as any));
      setRows(json);
      setParsed(true);
    } catch (err: any) {
      setError(
        t("app.import.parse_failed", {
          msg: err.message,
          fallback: "Failed: {msg}",
        }),
      );
    }
  };
  reader.readAsArrayBuffer(clImportFile2);
});
const doImport = () => {
  const h = hdrs;
  const fnIdx = findC2(
    h,
    "FirstName",
    "firstname",
    "first name",
    "Prénom",
  );
  const lnIdx = findC2(
    h,
    "LastName",
    "lastname",
    "last name",
    "Nom",
  );
  const nmIdx = findC2(h, "Name", "name", "Nom complet");
  const emIdx = findC2(h, "Email", "email", "E-mail");
  const phIdx = findC2(h, "Phone", "phone", "Téléphone");
  const stIdx = findC2(h, "Street", "street", "Rue");
  const pcIdx = findC2(
    h,
    "PostalCode",
    "postalcode",
    "postal code",
    "CP",
  );
  const ctIdx = findC2(h, "City", "city", "Ville");
  const coIdx2 = findC2(h, "Country", "country", "Pays");
  const vtIdx = findC2(h, "VAT", "vat", "TVA");
  const nrIdx = findC2(
    h,
    "National Register",
    "nationalregister",
    "Registre",
  );
  const lgIdx = findC2(
    h,
    "Language",
    "language",
    "Lang",
    "Langue",
  );
  const tpIdx = findC2(h, "Type", "type");
  const cnIdx = findC2(
    h,
    "CompanyName",
    "companyname",
    "company name",
    "Société",
  );
  const ttIdx = findC2(h, "Title", "title", "Titre");
  const qIdx = findC2(h, "Quantum", "quantum");
  const ntIdx = findC2(h, "Notes", "notes");
  // Phase 43 (item 4) — archived-client import columns.
  const csIdx = findC2(h, "Case", "case", "Dossier", "Affaire");
  const arIdx = findC2(
    h,
    "Archived",
    "archived",
    "Archivé",
    "Gearchiveerd",
  );
  const adIdx = findC2(
    h,
    "Archived Date",
    "archiveddate",
    "archived date",
    "Date d'archivage",
    "Archiveringsdatum",
  );
  const key = (c: any) => {
    const t = (c.type || "individual").toLowerCase();
    if (t === "company")
      return "co:" + (c.companyName || "").toLowerCase().trim();
    return (
      "in:" +
      (c.lastName || "").toLowerCase().trim() +
      "|" +
      (c.firstName || "").toLowerCase().trim()
    );
  };
  const existingByKey = new Map();
  clients.forEach((c: any) => {
    const k = key(c);
    if (k !== "co:" && k !== "in:|") existingByKey.set(k, c);
  });
  const newCls: any[] = [],
    updates = [],
    skippedRows: { row: number; reason: string }[] = []; // 214 addendum 4 §25 — typed: read from the write's closure
  /* C51 sweep — `imported++` below and the result toasts read
     `imported`/`skipped`, but a refactor renamed the buckets
     (newCls/skippedRows) and dropped these declarations; both
     had been free variables (ReferenceError on the first
     imported row / on the toast) just like the dialog's _fl.
     Found by the same no-undef pass. */
  let imported = 0;
  const seenKeys = new Set();
  // 05n §4 — THE TAP ABOVE THE CLEANUP. Every cell below that feeds a taxonomy
  // column is resolved to a CODE before it is written; an unresolvable one is
  // written through unchanged and REPORTED, never dropped and never guessed at.
  // See src/lib/importVocabulary.mjs for why that is the choice.
  const vocabReport = makeImportVocabularyReport();
  // A10 (brief 65) — src/lib/importCell.
  const cell = makeImportCell(vocabReport);
  for (let rIdx = 0; rIdx < rows.length; rIdx++) {
    const row = rows[rIdx];
    const excelRow = rIdx + 2;
    const vals = h.map((k: any) => row[k]);
    const fn = String(vals[fnIdx] || "").trim();
    const ln = String(vals[lnIdx] || "").trim();
    const nm = String(vals[nmIdx] || "").trim();
    const cn = String(vals[cnIdx] || "").trim();
    const typeRaw = String(vals[tpIdx] || "")
      .trim()
      .toLowerCase();
    const isCompany =
      typeRaw === "company" || (!!cn && !fn && !ln && !nm);
    if (!fn && !ln && !nm && !cn) {
      skippedRows.push({
        row: excelRow,
        reason: t("app.import.reason_empty_row", {
          fallback: "empty row — no First/Last/Company/Name",
        }),
      });
      continue;
    }
    let firstName = fn,
      lastName = ln;
    if (!isCompany && !firstName && !lastName && nm) {
      const parts = nm.split(/\s+/);
      firstName = parts[0] || "";
      lastName = parts.slice(1).join(" ") || "";
    }
    if (isCompany && !cn) {
      skippedRows.push({
        row: excelRow,
        reason: t("app.import.reason_company_missing", {
          fallback: "company row missing Company Name",
        }),
      });
      continue;
    }
    if (!isCompany && !lastName) {
      skippedRows.push({
        row: excelRow,
        reason: t("app.import.reason_individual_missing", {
          fallback: "individual row missing Last Name",
        }),
      });
      continue;
    }
    const q2 = vals[qIdx]
      ? parseFloat(String(vals[qIdx]).replace(",", "."))
      : null;
    newCls.push({
      id: tempId(),
      type:
        cell("client_type", _vClientType, vals[tpIdx], excelRow) || "individual",
      title: cell("person_title", _vTitle, vals[ttIdx], excelRow),
      firstName,
      lastName,
      email: String(vals[emIdx] || "").trim(),
      phone: String(vals[phIdx] || "").trim(),
      street: String(vals[stIdx] || "").trim(),
      postalCode: String(vals[pcIdx] || "").trim(),
      city: String(vals[ctIdx] || "").trim(),
      // 05n — was `|| "Belgium"`, twice. A sheet with no Country column made
      // every imported client Belgian, which is where prod's 790 came from.
      country: cell("country", _vCountry, vals[coIdx2], excelRow),
      nationalRegisterId: String(vals[nrIdx] || "").trim(),
      quantum: isNaN(q2 as any) ? null : q2,
      companyName: cn,
      vat: String(vals[vtIdx] || "").trim(),
      notes: String(vals[ntIdx] || "").trim(),
      // 209 §5 [RULED 18] — the file's language when its cell states one; none otherwise
      // (« fr » it was, written over any client the row updates): no language is ever assumed
      ...(String(vals[lgIdx] ?? "").trim() ? { lang: String(vals[lgIdx]).trim() } : {}),
      formerCaseRef: String(vals[csIdx] || "").trim(),
      archived: parseBoolCell(vals[arIdx]),
      archivedAt: parseDateCell(vals[adIdx]),
    });
    imported++;
  }
  const skipped = skippedRows.length;
  if (!imported) {
    toast(
      t("toast.no_valid_rows_skipped", withCount(skipped, "skipped", { fallback: "No valid rows (" + skipped + " skipped)",
      })),
    );
    setClImportFile2(null);
    return;
  }
  /* 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — « Replace all clients » DELETES every stored client (the engine's
     delete; the server refuses one linked to a matter, 409, and deletes the others): each is listed and the window
     waits; « Annuler » writes nothing. Add appends and runs at once. */
  const losses = newLossPlan();
  if (mode === "replace") addRowsTaken(losses, clients, "deleted", (r) => pname(r), (r) => { const o = r as { email?: unknown }; return typeof o.email === "string" ? o.email : ""; });
  const write = () => {
  if (mode === "replace") {
    persist(SK.cl, newCls, setClients);
  } else {
    persist(SK.cl, [...clients, ...newCls], setClients);
  }
  // Phase 43 — import COI/dedup at scale: match the imported rows
  // against the existing client pool (name + email, accent+space
  // normalized) and present a reviewable BATCH SUMMARY at
  // completion — never a per-row OK/Cancel modal loop.
  const matches = batchClientDuplicates(newCls, clients);
  const _vocabMsg = vocabReport.summary(t);
  if (_vocabMsg) toast("\u26a0 " + _vocabMsg);
  toast(
    t("toast.imported_n_of_clients", withCount(imported, "imported", { total: rows.length,
      skipped,
      fallback:
        "Imported " +
        imported +
        " of " +
        rows.length +
        " clients" +
        (skipped > 0 ? " (" + skipped + " skipped)" : ""),
    })),
  );
  setSummary({
    imported,
    total: rows.length,
    skipped,
    skippedRows,
    matches,
  });
  };
  if (hasLoss(losses)) {
    setLossAsk({ plan: losses, run: () => { setLossAsk(null); write(); } });
    return;
  }
  write();
};
if (lossAsk)
  return (
    <ImportLossDialog
      plan={lossAsk.plan}
      t={t as Translate}
      groupsSentence={(t as Translate)("import.loss.clients", withCount(lossAsk.plan.groups.length, "count", { fallback: `${String(lossAsk.plan.groups.length)} clients` }))}
      onAll={lossAsk.run}
      onCancel={() => { setLossAsk(null); }}
    />
  );
if (summary)
  return (
    <ModalLayout
      title={t("app.import.summary_title", {
        fallback: "Import complete",
      })}
      onClose={() => {
        setSummary(null);
        setClImportFile2(null);
      }}
      showX={false}
      closeOnOverlay
      maxWidth={620}
      footer={
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setSummary(null);
              setClImportFile2(null);
            }}
          >
            {t("common.done", { fallback: "Done" })}
          </button>
        </div>
      }
    >
          <p style={{ fontSize: FS.s11, marginBottom: 8 }}>
            {t("app.import.summary_counts", {
              imported: summary.imported,
              total: summary.total,
              skipped: summary.skipped,
              fallback:
                "Imported " +
                summary.imported +
                " of " +
                summary.total +
                (summary.skipped
                  ? " · " + summary.skipped + " skipped"
                  : "") +
                ".",
            })}
          </p>
          {summary.matches.length > 0 && (
            <>
              <div
                style={{
                  fontSize: FS.s10,
                  fontWeight: 600,
                  color: "var(--tm)",
                  margin: "8px 0 4px",
                }}
              >
                {t("app.import.matches_existing", {
                  n: summary.matches.length,
                  fallback:
                    summary.matches.length +
                    " imported row(s) may match an existing client",
                })}
              </div>
              <div
                className="table-wrap"
                style={{ maxHeight: 200, overflowY: "auto" }}
              >
                <Table style={{ width: "100%", fontSize: FS.s10 }}>
                  <thead>
                    <tr>
                      <th>
                        {t("app.import.col_imported", {
                          fallback: "Imported",
                        })}
                      </th>
                      <th>
                        {t("app.import.col_existing", {
                          fallback: "Existing client",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.matches.map((m: any, i: any) => (
                      <tr key={i}>
                        <td>{pname(m.candidate)}</td>
                        <td>
                          {m.matches
                            .map((c: any) => pname(c))
                            .join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
          {summary.skippedRows.length > 0 && (
            <>
              <div
                style={{
                  fontSize: FS.s10,
                  fontWeight: 600,
                  color: "var(--tm)",
                  margin: "10px 0 4px",
                }}
              >
                {t("app.import.skipped_rows", {
                  n: summary.skippedRows.length,
                  fallback:
                    summary.skippedRows.length + " row(s) skipped",
                })}
              </div>
              <div
                className="table-wrap"
                style={{ maxHeight: 160, overflowY: "auto" }}
              >
                <Table style={{ width: "100%", fontSize: FS.s10 }}>
                  <thead>
                    <tr>
                      <th>
                        {t("app.import.col_row", {
                          fallback: "Row",
                        })}
                      </th>
                      <th>
                        {t("app.import.col_reason", {
                          fallback: "Reason",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.skippedRows.map((sk: any, i: any) => (
                      <tr key={i}>
                        <td>{sk.row}</td>
                        <td>{sk.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
    </ModalLayout>
  );
if (error)
  return (
    <ImportErrorModal error={error} setCaImportFile={setClImportFile2} t={t} />
  );
if (!parsed)
  return (
    <ImportLoadingModal t={t} />
  );
return (
  <ModalLayout
    title={t("modal.import.clients_title", {
      fallback: "Import Clients",
    })}
    onClose={() => setClImportFile2(null)}
    showX={false}
    closeOnOverlay
    maxWidth={600}
    footer={
      <div className="modal-footer">
        <button
          type="button"
          className="modal-cancel"
          onClick={() => setClImportFile2(null)}
        >
          {t("common.cancel", { fallback: "Cancel" })}
        </button>
        <button type="button" className="modal-save" onClick={doImport}>
          {t("modal.import.confirm_n_clients", {
            count: rows.length,
            fallback: "✓ Import " + rows.length + " clients",
          })}
        </button>
      </div>
    }
  >
        <p style={{ fontSize: FS.s11, marginBottom: 10 }}>
          {t("app.import.found_rows", withCount(rows.length, "n", { fallback: "Found {n} rows.",
          }))}
        </p>
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 600,
              color: "var(--tm)",
              marginBottom: 6,
            }}
          >
            {t("app.import.mode_label", {
              fallback: "Import mode",
            })}
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              cursor: "pointer",
              marginBottom: 4,
            }}
          >
            <input
              type="radio"
              checked={mode === "add"}
              onChange={() => setMode("add")}
            />
            {t("app.client.add_to_existing", {
              fallback: "Add to existing clients",
            })}
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FS.s11,
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              checked={mode === "replace"}
              onChange={() => setMode("replace")}
            />
            <span>
              {t("app.import.replace_all", {
                fallback: "Replace all clients",
              })}{" "}
              <span style={{ color: "var(--rd)", fontSize: FS.s10 }}>
                {t("app.import.destructive", { fallback: "(destructive)" })}
              </span>
            </span>
          </label>
        </div>
        <div
          style={{
            fontSize: FS.s10,
            fontWeight: 600,
            color: "var(--tm)",
            marginBottom: 4,
          }}
        >
          {t("app.import.preview_first", withCount(Math.min(5, rows.length), "n", { fallback: "Preview (first {n} rows)",
          }))}
        </div>
        <div
          className="table-wrap"
          style={{ maxHeight: 200, overflowY: "auto" }}
        >
          <Table style={{ width: "100%" }}>
            <thead>
              <tr>
                {hdrs.slice(0, 7).map((h2: any) => (
                  <th
                    key={h2}
                    style={{
                      fontSize: FS.s8,
                      padding: "3px 4px",
                      color: "var(--tm)",
                    }}
                  >
                    {h2}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 5).map((row: any, i: any) => (
                <tr key={i}>
                  {hdrs.slice(0, 7).map((h2: any) => (
                    <td
                      key={h2}
                      style={{
                        fontSize: FS.s9,
                        padding: "2px 4px",
                        maxWidth: 100,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {String(row[h2] || "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
  </ModalLayout>
);
}
